import { Wallet } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `requiredPermission: 'payment.read'` matches the `NavPermission` that
 * gates "Cobranzas" in `layout/navigation.ts`. Every quoted label and
 * button below was cross-checked against the real screens:
 * `payment-list-page.tsx`, `register-payment-form.tsx`,
 * `payment-detail-page.tsx`, `components/allocate-payment-modal.tsx`,
 * `components/void-payment-modal.tsx`, `components/withholding-line-editor.tsx`,
 * `customers/customer-account-page.tsx`, `customers/customer-aging-page.tsx`
 * and the "Saldo" column in `invoices/invoice-list-page.tsx`.
 */
export const PAYMENTS_MODULE: GuideModule = {
  id: 'payments',
  title: 'Cobranzas',
  icon: Wallet,
  requiredPermission: 'payment.read',
  priority: 10,
  summary:
    'Acá registrás los recibos de cobro de tus clientes y los imputás a las facturas que corresponden. Un recibo puede llevar retenciones y percepciones, y no hace falta imputarlo completo el mismo día: lo que no imputás queda como crédito a favor del cliente, disponible para más adelante. El sistema te sugiere imputar primero las facturas más antiguas, pero siempre podés ajustar los montos. Desde el detalle de cada cliente también podés consultar su cuenta corriente completa y su antigüedad de deuda, para saber cuánto debe y desde cuándo.',
  steps: [
    {
      title: 'Registrar un recibo de cobro',
      description:
        'Andá a Cobranzas y hacé clic en "Registrar recibo" (también disponible desde la pestaña "Cuenta corriente" de un cliente, ya con el cliente preseleccionado). Completá "Cliente", "Fecha de pago", "N° de operación, cheque o transferencia", "Método" (se elige de la lista de medios de pago activos que se administra en Configuración → "Medios de Pago") y "Monto recibido", y guardá. Un recibo ya registrado no se puede editar después.',
      keyTerms: ['recibo'],
    },
    {
      title: 'Cargar retenciones y percepciones',
      description:
        'En el mismo formulario, en "Retenciones y percepciones", hacé clic en "Agregar retención" por cada una que corresponda. Elegí el "Tipo" de la lista de tipos de retención activos (Configuración → "Tipos de Retención"); si el tipo tiene jurisdicción y alícuota por defecto, el sistema completa la "Jurisdicción (si corresponde)" y sugiere el "Monto" calculado sobre el "Monto recibido" (solo si todavía no lo cargaste), y vos podés corregir ambos. Completá el "Número de certificado" (obligatorio). El total que se puede imputar es el monto recibido más la suma de las retenciones.',
      keyTerms: ['retencion'],
    },
    {
      title: 'Imputar el recibo a facturas',
      description:
        'Desde el detalle del recibo, hacé clic en "Imputar a facturas". El sistema te propone imputar primero las facturas más antiguas del cliente, con un monto sugerido para cada una y su "Saldo pendiente" a la vista. Podés ajustar cualquiera de los montos antes de confirmar con "Confirmar imputación": el total no puede superar lo disponible del recibo, y ninguna línea puede superar el saldo pendiente de esa factura.',
      keyTerms: ['imputacion'],
    },
    {
      title: 'Pagos parciales y crédito a favor',
      description:
        'Si imputás menos de lo que debe una factura, esa factura queda en "Pago parcial" con el resto todavía pendiente. Si te sobra monto del recibo sin imputar a ninguna factura, queda como saldo a favor del cliente: lo vas a ver en el detalle del recibo como "Sin imputar" y en la cuenta corriente del cliente como "Crédito no imputado", listo para usarse en una imputación futura.',
      keyTerms: ['saldo-a-favor'],
    },
    {
      title: 'Anular un recibo',
      description:
        'Solo Administración y Superadministración pueden anular un recibo: hacé clic en "Anular" (en el listado o en el detalle) y completá el motivo, que es obligatorio. Al confirmar, las facturas que este recibo tenía imputadas vuelven a su estado pendiente por el monto que les cubría. Es una acción que no se puede deshacer.',
      keyTerms: ['recibo'],
    },
    {
      title: 'Consultar la cuenta corriente de un cliente',
      description:
        'Entrá al detalle de una empresa y abrí la pestaña "Cuenta corriente". Arriba vas a ver el resumen (Facturado, Cobrado, Pendiente, Vencido y Crédito no imputado) y debajo el detalle de movimientos, cada uno con su tipo ("Saldo inicial", "Factura" o "Recibo"), fecha, comprobante, débito, crédito y saldo acumulado. Podés filtrar el listado por "Desde" y "Hasta"; el resumen siempre refleja la situación actual del cliente, sin importar el filtro de fechas.',
      keyTerms: ['saldo-inicial'],
    },
    {
      title: 'Consultar la antigüedad de deuda',
      description:
        'Desde la pestaña "Antigüedad de deuda" del cliente, vas a ver su deuda pendiente agrupada en tramos: "Corriente", "1 a 30 días", "31 a 60 días", "61 a 90 días" y "Más de 90 días", cada uno con la cantidad de facturas y el monto total. El tramo "Más de 90 días" se resalta si tiene deuda, por ser la más crítica. Hacé clic sobre un tramo para desplegar el detalle de sus facturas, con su vencimiento y saldo.',
      keyTerms: ['antiguedad-de-deuda'],
    },
    {
      title: 'Facturas vencidas y la columna Saldo',
      description:
        'Todas las noches, el sistema revisa las facturas emitidas con saldo pendiente y las marca automáticamente como "Vencida" si ya pasó su fecha de vencimiento, sin que nadie tenga que hacerlo a mano. En el listado de Facturación, la columna "Saldo" muestra cuánto falta cobrar de cada factura emitida (o con pago parcial, pagada o vencida); en un borrador o una factura rechazada, que todavía no generan saldo, se ve un guion.',
      keyTerms: ['factura-vencida'],
    },
  ],
  states: [
    { value: 'RECORDED', label: 'Registrado', description: 'El recibo se cargó y todavía no fue imputado a ninguna factura, o tiene saldo sin imputar.' },
    { value: 'CLEARED', label: 'Imputado', description: 'Todo el monto disponible del recibo ya fue imputado a una o más facturas.' },
    { value: 'VOIDED', label: 'Anulado', description: 'El recibo fue anulado por Administración o Superadministración; sus imputaciones se revirtieron.' },
  ],
  faq: [
    {
      question: '¿Quién puede anular un recibo?',
      answer: 'Solo los roles Administración y Superadministración tienen el botón "Anular" disponible.',
    },
    {
      question: '¿De dónde salen las opciones de "Método" y de "Tipo" de retención?',
      answer:
        'De los catálogos de Configuración → "Medios de Pago" y "Tipos de Retención". Si falta una opción, quien tenga el permiso "Modificar configuración del sistema" puede agregarla; el recibo guarda el nombre elegido aunque después se renombre o desactive.',
    },
    {
      question: '¿Cómo decide el sistema qué facturas sugerir al imputar un recibo?',
      answer:
        'Te propone imputar primero las facturas más antiguas del cliente (las de vencimiento más próximo o ya vencido), pero es solo una sugerencia: podés cambiar el monto de cualquier línea antes de confirmar.',
    },
    {
      question: '¿Qué pasa si no imputo todo el monto de un recibo?',
      answer:
        'Lo que quede sin imputar no se pierde: queda disponible como crédito a favor del cliente, visible en su cuenta corriente como "Crédito no imputado", para usarlo en una imputación posterior.',
    },
    {
      question: '¿Puedo editar un recibo después de registrarlo?',
      answer:
        'No. Un recibo es inmutable una vez registrado. Si te equivocaste, tenés que anularlo (con Administración o Superadministración) y registrar uno nuevo con los datos correctos.',
    },
    {
      question: '¿Cuándo se marca una factura como "Vencida"?',
      answer:
        'El sistema lo revisa automáticamente todas las noches: cualquier factura emitida con saldo pendiente cuya fecha de vencimiento ya pasó se marca "Vencida", sin que haga falta ninguna acción manual.',
    },
    {
      question: '¿Qué es el saldo inicial que aparece en la cuenta corriente?',
      answer:
        'Es la deuda (o el crédito) que un cliente ya tenía al momento de empezar a usar el sistema, cargada una sola vez para que su cuenta corriente arranque completa desde el primer día, sin perder ese historial previo.',
    },
  ],
  tips: [
    'Antes de anular un recibo, revisá sus imputaciones en el detalle: esas facturas van a volver a estado pendiente por el monto que este recibo les cubría.',
    'Si un cliente tiene "Crédito no imputado" en su cuenta corriente, podés usarlo al registrar la imputación de su próxima factura pendiente.',
    'La antigüedad de deuda es la vista más rápida para priorizar a quién reclamarle el pago: empezá siempre por el tramo "Más de 90 días".',
  ],
};
